'use client'

import { Input } from '@/components/ui/input'
import { ListSkeleton } from '@/components/ui/skeletons'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ORGANIZATION_CREATION_LIMIT_PER_USER } from '@/config/plans'
import { cn } from '@/lib/utils'
import { isPlatformUser } from '@/stores/auth'
import { useIsLoading, useUser } from '@/stores/authStore'
import { isConsoleDeployment, isDemoDeployment } from '@/utils/deployment'
import { getCreatedOrganizationsCount, useIsOwner } from '@/utils/permissions'
import { Check, Pin, Plus, Search } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useDefaultOrganization, useOrganizations, useSetDefaultOrganization } from './useSwitcherData'

interface OrganizationSwitcherProps {
	currentOrgId: string | null
	onOrganizationSelect: (orgId: string) => void
	onSuccess?: () => void
	onCreateOrgClick: () => void
}

export function OrganizationSwitcher({
	currentOrgId,
	onOrganizationSelect,
	onSuccess,
	onCreateOrgClick,
}: OrganizationSwitcherProps) {
	const [searchTerm, setSearchTerm] = useState('')
	const isOwner = useIsOwner()
	const user = useUser()
	const isLoading = useIsLoading()

	const orgCount = user ? getCreatedOrganizationsCount(user) : 0
	const isLimitReached = !isDemoDeployment() && orgCount >= ORGANIZATION_CREATION_LIMIT_PER_USER

	const { filteredOrganizations, isFiltered } = useOrganizations(searchTerm)
	const defaultOrgId = useDefaultOrganization()
	const { setDefault, isPinning } = useSetDefaultOrganization()

	const handleOrganizationSwitch = useCallback(
		(orgId: string) => {
			onOrganizationSelect(orgId)
			onSuccess?.()
		},
		[onOrganizationSelect, onSuccess],
	)

	const handlePinClick = useCallback(
		(e: React.MouseEvent, orgId: string) => {
			e.stopPropagation()
			if (isPinning) {
				return
			}
			const newDefault = orgId === defaultOrgId ? null : orgId
			setDefault(newDefault)
		},
		[defaultOrgId, setDefault, isPinning],
	)

	const isPlatformDeploymentUser = isPlatformUser(user)
	// Pins are personal (stored in user settings), so any platform user can pin — not just owners.
	const canPinDefaultOrganization = isPlatformDeploymentUser && !isConsoleDeployment()

	return (
		<>
			<div className='w-full'>
				<div className='relative mb-2'>
					<Search className='absolute left-3 top-1/2 -translate-y-1/2 icon-alone text-muted-foreground' />
					<Input
						placeholder='Find organization...'
						value={searchTerm}
						onChange={e => setSearchTerm(e.target.value)}
						className='h-9 pl-9 bg-transparent border-0 border-b border-border rounded-none text-body placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-border'
					/>
				</div>

				<div className='space-y-0.5 max-h-48 overflow-y-auto py-1'>
					{isLoading ? (
						<ListSkeleton count={3} itemHeight='h-10' />
					) : filteredOrganizations.length > 0 ? (
						filteredOrganizations.map(org => {
							const isOrgActive = org.id === currentOrgId
							const isPinned = org.id === defaultOrgId
							return (
								<div key={org.id} className='space-y-0.5'>
									<button
										type='button'
										className={cn(
											'group/item w-full flex items-center justify-between p-[var(--switcher-item-padding)] text-body text-left rounded-md transition-colors cursor-pointer',
											isOrgActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground',
										)}
										onClick={() => handleOrganizationSwitch(org.id)}>
										<div className='flex items-center gap-2 flex-1 min-w-0'>
											<span className='text-foreground truncate'>{org.name}</span>
											{isPlatformDeploymentUser && (
												<span
													className={
														'text-badge leading-tight px-1 py-px rounded uppercase font-medium shrink-0 bg-muted text-muted-foreground'
													}>
													{org.isOwner ? 'Owner' : 'Member'}
												</span>
											)}
										</div>
										<div className='flex items-center gap-1 ml-2 shrink-0'>
											{canPinDefaultOrganization && (
												<button
													type='button'
													className={cn(
														'p-0.5 rounded-sm transition-opacity cursor-pointer',
														isPinned
															? 'opacity-100 text-primary'
															: 'opacity-0 group-hover/item:opacity-100 text-muted-foreground hover:text-foreground',
													)}
													onClick={e => handlePinClick(e, org.id)}
													disabled={isPinning}
													title={isPinned ? 'Unpin default organization' : 'Set as default organization'}>
													<Pin
														className={cn(
															'size-(--icon-standalone) rotate-45 transform shrink-0',
															isPinned && 'text-primary',
														)}
														fill={isPinned ? 'currentColor' : 'none'}
														strokeWidth={2}
													/>
												</button>
											)}
											{isOrgActive && <Check className='icon-alone text-foreground' strokeWidth={2} />}
										</div>
									</button>
								</div>
							)
						})
					) : (
						<div className='text-center text-body text-muted-foreground py-4'>
							{isFiltered ? 'No organizations found' : 'No organizations available'}
						</div>
					)}
				</div>

				{isOwner && (
					<div className='border-t border-border mt-1 pt-1 space-y-0.5'>
						{isLimitReached ? (
							<Tooltip>
								<TooltipTrigger asChild>
									<div className='w-full cursor-not-allowed'>
										<button
											type='button'
											className='w-full flex items-center gap-2 p-[var(--switcher-item-padding)] text-body text-left rounded-md transition-colors text-muted-foreground pointer-events-none opacity-50'
											disabled={true}>
											<Plus className='icon-alone' strokeWidth={2} />
											New organization
										</button>
									</div>
								</TooltipTrigger>
								<TooltipContent side='right'>
									<div className='flex flex-col gap-0.5'>
										<p>You have reached the maximum limit of {ORGANIZATION_CREATION_LIMIT_PER_USER} organizations.</p>
										<p>Please delete an existing organization to create a new one.</p>
									</div>
								</TooltipContent>
							</Tooltip>
						) : (
							<button
								type='button'
								className='w-full flex items-center gap-2 p-[var(--switcher-item-padding)] text-body text-left rounded-md transition-colors cursor-pointer hover:bg-accent hover:text-accent-foreground text-foreground'
								onClick={onCreateOrgClick}>
								<Plus className='icon-alone' strokeWidth={2} />
								New organization
							</button>
						)}
					</div>
				)}
			</div>
		</>
	)
}
